import { Component, Input, ViewChild } from '@angular/core';
import { SharedModule } from 'src/app/layouts/full/shared/shared.module';
import { AnalysisComponent } from '../analysis/analysis.component';
import { User } from 'src/app/models/user.model';
import { Scan } from 'src/app/models/scan.model';
import { Analysis, DeviceStatus, Patient, SensingtexService } from 'src/app/services/sensingtex.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ScanService } from 'src/app/services/scan.service';
import { UserService } from 'src/app/services/user.service';
import { ServicesService } from 'src/app/services/services.service';
import { filter, forkJoin } from 'rxjs';
import { invertFootData } from './pressureMapHelpers';
import Swal from 'sweetalert2';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ModalProFeaturesComponent } from '../modal-pro-features/modal-pro-features.component';

@Component({
  selector: 'app-analysis-details',
  standalone: true,
  imports: [SharedModule, AnalysisComponent, ModalProFeaturesComponent],
  templateUrl: './analysis-details.component.html',
  styleUrl: './analysis-details.component.scss'
})
export class AnalysisDetailsComponent {
  @ViewChild('analysisComponent') analysisComponent: AnalysisComponent;
  @Input() showActions: boolean = true

  public user: User;
  orderId: string;
  order: Scan = null;
  analysis: Analysis = null;
  analysisId: string
  isLoading: boolean = false
  generatingPDF: boolean = false
  userServices: any = []
  premiumUser: boolean = false
  dynamicNeedsRotation: boolean = false
  connectedPlatform: boolean = false


  patients: Patient[] = []
  patientAnalyses: Analysis[] = []
  selectedPatient: Patient
  visible: boolean = false
  staticModalVisible: boolean = false
  dynamicModalVisible: boolean = false
  balanceModalVisible: boolean = false
  upgradeModalVisible: boolean = false

  constructor(
    private route: ActivatedRoute,
    private orderService: ScanService,
    private sensingtexService: SensingtexService,
    private userService: UserService,
    private router: Router,
    private servicesService: ServicesService
  ) {

    // Get data from the URL path
    this.route.params.subscribe(params => {
      this.orderId = params['orderId'];
      this.analysisId = params['analysisId'];
    });

    // Scroll to top on click some analysis
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.scrollToTop();
    });
  }

  ngOnInit(): void {
    this.user = this.userService.user
    this.loadRequiredData();
    this.checkServices()
  }

  onDynamicNeedsRotation() {
    // this.dynamicNeedsRotation = true
  }

  checkServices() {
    this.servicesService.getServices().subscribe((services) => {
      this.userServices = services;
      this.premiumUser = this.userServices.some(service => service.name === 'Pro features' && service.active);
    })
  }

  loadRequiredData(analysisId?: string) {

    // Load all the patients only when no patient selected
    if (!this.selectedPatient) {
      // Load sensingtex Patients
      this.loadPatients()
    } else {

      if (this.patientAnalyses && this.patientAnalyses.length > 0) {
        this.analysis = null;
        setTimeout(() => {
          this.analysis = this.patientAnalyses.find(a => a.id === analysisId);
        }, 1);
      }
    }
  }

  loadPatients() {
    this.isLoading = true

    forkJoin([
      this.orderService.getOne(this.orderId),
      this.sensingtexService.getPatients(this.user.sensingtexManagerId)
    ]).subscribe(([scan, patients]) => {
      this.order = scan;
      this.patients = patients;

      const matchingPatient = patients.find(patient => patient.id === this.order?.sensingtexPatientId);

      if (matchingPatient) {
        this.selectedPatient = matchingPatient;
        this.loadPatientAnalysis(matchingPatient.id);
      } else {
        this.selectedPatient = null;
      }
    });
  }

  loadPatientAnalysis(patientId: string) {
    this.sensingtexService.getAllAnalysesByPatientId(patientId, this.user.sensingtexManagerId, this.user.sensingtexHardwareId).subscribe(patientAnalyses => {
      this.patientAnalyses = patientAnalyses.map(patientAnalysis => {

        if (patientAnalysis.type === "Dynamic") {
          patientAnalysis.additionalParameters[12]["activePointsLeft"] = invertFootData(patientAnalysis.additionalParameters[12]["activePointsLeft"], { x: false, y: false });
          patientAnalysis.additionalParameters[13]["activePointsRight"] = invertFootData(patientAnalysis.additionalParameters[13]["activePointsRight"], { x: true, y: true });
        }

        return {
          ...patientAnalysis,
          creationDate: this.formatCreationDate(patientAnalysis.creationDate)
        }
      })

      if (this.patientAnalyses && this.patientAnalyses.length > 0) {
        this.analysis = this.patientAnalyses.find(a => a.id === this.analysisId);
      }
      this.isLoading = false
    })
  }

  startAnalysis() {
    this.visible = true;

    this.sensingtexService.getUserDevices(this.user.sensingtexManagerId).subscribe({
      next: (userDevices) => {
        const userDeviceId = userDevices[0]._id.$oid

        this.sensingtexService.getDeviceStatus(userDeviceId).subscribe({
          next: (userDeviceStatus: DeviceStatus) => {
            if (userDeviceStatus.deviceFunctional) {
              this.connectedPlatform = true
            } else {
              this.connectedPlatform = false
            }
          },
          error: (err) => {
            console.error("Error al obtener el estado de la plataforma: ", err)
          }
        })
      },
      error: (err) => {
        console.error("Error al obtener los user devices: ", err)
      }
    })

    if (this.order.sensingtexPatientId) {
      this.sensingtexService.selectPatientToDoAnalysis(this.selectedPatient, this.user.sensingtexManagerId).subscribe(
        () => {
          console.log("Paciente seleccionado para realizar análisis");
        },
        (error) => {
          console.error("Error al seleccionar paciente para realizar análisis:", error);
        }
      );
    } else {
      const name = this.order.data["Patient Data"].name;
      let weight = this.order.data["Patient Data"].weight;
      let height = this.order.data["Patient Data"].height;

      if (!weight || weight === 0) {
        weight = 70;
      }

      if (!height || height === 0) {
        height = 170;
      }

      this.sensingtexService.createPatient({ name, weight, height }, this.user.sensingtexManagerId).subscribe(
        (response) => {
          console.log("Paciente creado con éxito:", response);

          const newPatientId = response.id;

          this.orderService.updateSensingtexPatientId(this.orderId, newPatientId).subscribe(
            () => {
              console.log("order.sensingtexPatientId actualizado con éxito");
            },
            (error) => {
              console.error("Error al actualizar order.sensingtexPatientId:", error);
            }
          );

          const newPatient = {
            id: newPatientId,
            isSelected: false,
            managerId: this.user.sensingtexManagerId,
            name: name,
            weight: weight,
            height: height
          }

          this.sensingtexService.selectPatientToDoAnalysis(newPatient, this.user.sensingtexManagerId).subscribe(
            () => {
              this.selectedPatient = newPatient
              console.log("Paciente seleccionado para realizar análisis");
            },
            (error) => {
              console.error("Error al seleccionar paciente para realizar análisis:", error);
            }
          );
        },
        (error) => {
          console.error("Error al crear paciente:", error);
        }
      );
    }
  }

  requestAnalysis(analysisType) {

    const staticTimerSeconds = 3
    const dynamicTimerSeconds = 20
    const balanceTimerSeconds = 15

    this.visible = false

    if (analysisType === "static") {
      this.staticModalVisible = false

      this.sensingtexService.requestAnalysis(this.selectedPatient, analysisType, this.user.sensingtexHardwareSerial, this.user.sensingtexManagerId).subscribe()
      Swal.fire({
        title: "Doing analysis!",
        html: "Stand on top of the device for 3 seconds.",
        timer: staticTimerSeconds * 1000,
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading();
        },
        willClose: () => {
          this.loadPatients()
        }
      })
    }

    if (analysisType === "dynamic") {
      this.dynamicModalVisible = false

      this.sensingtexService.requestAnalysis(this.selectedPatient, analysisType, this.user.sensingtexHardwareSerial, this.user.sensingtexManagerId).subscribe()
      Swal.fire({
        title: "Doing analysis!",
        html: "You have 20 seconds to follow the steps.",
        timer: dynamicTimerSeconds * 1000,
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading();
        },
        willClose: () => {
          this.loadPatients()
        }
      })
    }

    if (analysisType === "balance") {
      this.balanceModalVisible = false

      this.sensingtexService.requestAnalysis(this.selectedPatient, analysisType, this.user.sensingtexHardwareSerial, this.user.sensingtexManagerId).subscribe()
      Swal.fire({
        title: "Doing analysis!",
        html: "Stand on top of the device for 15 seconds.",
        timer: balanceTimerSeconds * 1000,
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading();
        },
        willClose: () => {
          this.loadPatients()
        }
      })
    }
  }

  formatCreationDate(rawDate: string): string {
    const date = new Date(rawDate);

    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    const formattedDate = `${month} ${day} ${year} ${hours}:${minutes}:${seconds}`;

    return formattedDate;
  }

  goToAnalysisDetails(orderId: string, analysisId: string): void {
    this.router.navigate(['/analysis-details', orderId, analysisId]).then(() => {
      this.loadRequiredData(analysisId);
    })
  }

  deleteAnalysis(analysisId: string) {
    Swal.fire({
      title: 'Are you sure you want to delete this analysis?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete the analysis.',
      cancelButtonText: 'No, go back.',
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Removing analysis'
        })
        Swal.showLoading()
        setTimeout(() => {
          this.sensingtexService.deleteAnalysis(analysisId).subscribe(
            (resp) => {
              this.loadPatients();
              Swal.fire('Analysis deleted successfully', '', 'success');
            },
            (err) => {
              Swal.fire('Error', err.error.message, 'error');
            }
          );
        }, 2000);

      }
    });
  }

  generateAnalysisDetailsPDF() {
    this.generatingPDF = true;

    const widthScaleFactor = 0.8;

    setTimeout(() => {
      const elementToPrint = document.getElementById('analysis-details-pdf');

      html2canvas(elementToPrint, { scale: 2 }).then((canvas) => {
        const pdf = new jsPDF('p', 'pt', 'a4');
        const patientName = this.order.data['Patient Data']['name'];
        const patientSurname = this.order.data['Patient Data']['surname'];
        const analysisType = this.analysis.type;
        const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });

        // Calcular las dimensiones de la página PDF
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        // Calcular el espacio en blanco requerido arriba del documento
        const marginHeight = pdfHeight * 0.1;

        // Calcular las dimensiones de la imagen para ajustarla al PDF
        const scaleFactorWidth = (pdfWidth / canvas.width) * widthScaleFactor;
        const scaleFactorHeight = (pdfHeight - marginHeight) / canvas.height;
        const scaleFactor = Math.min(scaleFactorWidth, scaleFactorHeight);
        const imageWidth = canvas.width * scaleFactor;
        const imageHeight = canvas.height * scaleFactor;

        // Calcular la posición para centrar la imagen horizontalmente en el PDF
        const x = (pdfWidth - imageWidth) / 2;

        // Calcular la posición para colocar la imagen después del espacio en blanco
        const y = marginHeight;

        // Añadir la imagen al PDF
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, imageWidth, imageHeight);

        // Añadir otros elementos, como textos, al PDF
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Patient name: ', 20, 30);
        pdf.setFont('helvetica', '');
        pdf.text(`${patientName} ${patientSurname}`, 89, 30);
        pdf.setFont('helvetica', 'bold');
        pdf.text('PDF creation date: ', 20, 50);
        pdf.setFont('helvetica', '');
        pdf.text(currentDate, 112, 50);

        // Guardar el PDF
        pdf.save(`${patientName}_${patientSurname}_${analysisType}.pdf`);

        this.generatingPDF = false;
      });
    }, 100);
  }


  openStaticModal() {
    this.staticModalVisible = true
  }

  openDynamicModal() {
    this.dynamicModalVisible = true
  }

  openBalanceModal() {
    this.balanceModalVisible = true
  }

  openUpgradeModal() {
    this.upgradeModalVisible = true
  }

  scrollToTop() {
    window.scrollTo(0, 0);
  }
}
